import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';

export type ThemeMode = 'system' | 'light' | 'dark';
export type ThemePalette = 'azure' | 'violet' | 'green' | 'cyan' | 'orange' | 'rose';

/** Couleurs principales proposées ; `swatch` : teintes 40 / 80 de la palette Material (aperçu clair / sombre). */
export const THEME_PALETTES: { value: ThemePalette; label: string; swatch: [string, string] }[] = [
  { value: 'azure', label: 'Azur (par défaut)', swatch: ['#005cbb', '#abc7ff'] },
  { value: 'violet', label: 'Violet', swatch: ['#7d00fa', '#d5baff'] },
  { value: 'green', label: 'Vert', swatch: ['#026e00', '#02e600'] },
  { value: 'cyan', label: 'Cyan', swatch: ['#006a6a', '#00dddd'] },
  { value: 'orange', label: 'Orange', swatch: ['#964900', '#ffb787'] },
  { value: 'rose', label: 'Rose', swatch: ['#ba005c', '#ffb1c5'] },
];

const MODE_KEY = 'theme.mode';
const PALETTE_KEY = 'theme.palette';

function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key) as T | null;
    return value && allowed.includes(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Préférence non conservée (navigation privée, stockage bloqué) : appliquée pour la session seulement.
  }
}

/** Thème de l'utilisateur (mode clair/sombre et couleur principale), propre à ce navigateur. */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly root = inject(DOCUMENT).documentElement;

  readonly mode = signal<ThemeMode>(read(MODE_KEY, ['system', 'light', 'dark'], 'system'));
  readonly palette = signal<ThemePalette>(
    read(
      PALETTE_KEY,
      THEME_PALETTES.map((p) => p.value),
      'azure',
    ),
  );

  constructor() {
    this.apply();
  }

  setMode(mode: ThemeMode): void {
    this.mode.set(mode);
    write(MODE_KEY, mode);
    this.apply();
  }

  setPalette(palette: ThemePalette): void {
    this.palette.set(palette);
    write(PALETTE_KEY, palette);
    this.apply();
  }

  private apply(): void {
    const mode = this.mode();
    if (mode === 'system') {
      delete this.root.dataset['theme'];
    } else {
      this.root.dataset['theme'] = mode;
    }
    const palette = this.palette();
    if (palette === 'azure') {
      delete this.root.dataset['palette'];
    } else {
      this.root.dataset['palette'] = palette;
    }
  }
}
